/**
 * Contact Section Component
 *
 * React implementation of the contact section renderer.
 * Supports variants: full, minimal, split
 */

import { ContactData, SectionProps, EditableElement as EditableElementType } from '../types';
import { EditableText, EditableImage } from '../components/EditableElement';

// =============================================================================
// TYPES
// =============================================================================

interface ContactProps extends SectionProps<ContactData> {}

// =============================================================================
// HELPER: Find element by path
// =============================================================================

function findElement(elements: EditableElementType[] | undefined, path: string): EditableElementType | undefined {
  return elements?.find(el => el.path === path);
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function Contact({ data, ctx, sectionId, elements }: ContactProps) {
  const variant = data.variant || 'full';

  switch (variant) {
    case 'minimal':
      return <ContactMinimal data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
    case 'split':
      return <ContactSplit data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
    default:
      return <ContactFull data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
  }
}

// =============================================================================
// FULL VARIANT
// =============================================================================

function ContactFull({ data, ctx, elements }: ContactProps) {
  const { recipe } = ctx;

  const eyebrowEl = findElement(elements, 'eyebrow');
  const headlineEl = findElement(elements, 'headline');
  const subheadlineEl = findElement(elements, 'subheadline');

  return (
    <section
      id="contact"
      className="py-20 lg:py-28"
      style={{
        backgroundColor: recipe.colors.surface,
        fontFamily: `${recipe.typography.bodyFont}, sans-serif`,
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div>
            {(data.eyebrow || eyebrowEl) && (
              <div className="mb-4">
                {eyebrowEl ? (
                  <EditableText
                    element={eyebrowEl}
                    as="span"
                    className="text-sm font-semibold uppercase tracking-wider"
                    style={{ color: recipe.colors.primary }}
                  />
                ) : (
                  <span
                    className="text-sm font-semibold uppercase tracking-wider"
                    style={{ color: recipe.colors.primary }}
                  >
                    {data.eyebrow}
                  </span>
                )}
              </div>
            )}

            {headlineEl ? (
              <EditableText
                element={headlineEl}
                as="h2"
                className="text-3xl md:text-4xl font-bold mb-4"
                style={{
                  color: recipe.colors.text,
                  fontFamily: `${recipe.typography.headingFont}, sans-serif`,
                }}
              />
            ) : data.headline ? (
              <h2
                className="text-3xl md:text-4xl font-bold mb-4"
                style={{
                  color: recipe.colors.text,
                  fontFamily: `${recipe.typography.headingFont}, sans-serif`,
                }}
              >
                {data.headline}
              </h2>
            ) : null}

            {subheadlineEl ? (
              <EditableText
                element={subheadlineEl}
                as="p"
                className="text-lg mb-8"
                style={{ color: recipe.colors.textMuted }}
              />
            ) : data.subheadline ? (
              <p className="text-lg mb-8" style={{ color: recipe.colors.textMuted }}>
                {data.subheadline}
              </p>
            ) : null}

            <div className="space-y-6">
              {/* Phone */}
              {data.phone && (
                <a href={`tel:${data.phone}`} className="flex items-center gap-4">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${recipe.colors.primary}15`, color: recipe.colors.primary }}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm" style={{ color: recipe.colors.textMuted }}>Phone</p>
                    <p className="font-semibold" style={{ color: recipe.colors.text }}>{data.phone}</p>
                  </div>
                </a>
              )}

              {/* Email */}
              {data.email && (
                <a href={`mailto:${data.email}`} className="flex items-center gap-4">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${recipe.colors.primary}15`, color: recipe.colors.primary }}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm" style={{ color: recipe.colors.textMuted }}>Email</p>
                    <p className="font-semibold" style={{ color: recipe.colors.text }}>{data.email}</p>
                  </div>
                </a>
              )}

              {/* Address */}
              {data.address && (
                <div className="flex items-center gap-4">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${recipe.colors.primary}15`, color: recipe.colors.primary }}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm" style={{ color: recipe.colors.textMuted }}>Address</p>
                    <p className="font-semibold" style={{ color: recipe.colors.text }}>
                      {data.address.street}, {data.address.city}, {data.address.state} {data.address.zip}
                    </p>
                  </div>
                </div>
              )}

              {/* Hours */}
              {data.hours && data.hours.length > 0 && (
                <div className="flex items-start gap-4">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${recipe.colors.primary}15`, color: recipe.colors.primary }}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm mb-2" style={{ color: recipe.colors.textMuted }}>Hours</p>
                    <div className="space-y-1">
                      {data.hours.map((h, i) => (
                        <div key={i} className="flex gap-4 text-sm">
                          <span className="w-24" style={{ color: recipe.colors.text }}>{h.day}</span>
                          <span style={{ color: recipe.colors.textMuted }}>{h.hours}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Social */}
            {data.social && Object.keys(data.social).length > 0 && (
              <div className="mt-8 pt-8 border-t" style={{ borderColor: `${recipe.colors.textMuted}20` }}>
                <p className="text-sm mb-4" style={{ color: recipe.colors.textMuted }}>Follow us</p>
                <div className="flex gap-4">
                  {data.social.facebook && (
                    <a href={data.social.facebook} target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" style={{ color: recipe.colors.text }}>
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                      </svg>
                    </a>
                  )}
                  {data.social.instagram && (
                    <a href={data.social.instagram} target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" style={{ color: recipe.colors.text }}>
                        <path d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" />
                      </svg>
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Contact Form */}
          <div
            className="p-8 rounded-xl"
            style={{
              backgroundColor: recipe.colors.background,
              borderRadius: recipe.borders.cardRadius,
              boxShadow: recipe.shadows.card,
            }}
          >
            <h3 className="text-xl font-semibold mb-6" style={{ color: recipe.colors.text }}>
              Send us a message
            </h3>
            <form action={data.formEndpoint} method="POST" className="space-y-4">
              {data.formFields?.map((field, i) => (
                <div key={i}>
                  <label
                    htmlFor={field.name}
                    className="block text-sm font-medium mb-1"
                    style={{ color: recipe.colors.text }}
                  >
                    {field.label}
                    {field.required && <span className="text-red-500">*</span>}
                  </label>
                  {field.type === 'textarea' ? (
                    <textarea
                      id={field.name}
                      name={field.name}
                      required={field.required}
                      rows={4}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:outline-none"
                      style={{
                        borderColor: `${recipe.colors.textMuted}40`,
                        borderRadius: recipe.borders.inputRadius,
                      }}
                    />
                  ) : field.type === 'select' ? (
                    <select
                      id={field.name}
                      name={field.name}
                      required={field.required}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:outline-none"
                      style={{
                        borderColor: `${recipe.colors.textMuted}40`,
                        borderRadius: recipe.borders.inputRadius,
                      }}
                    >
                      <option value="">Select...</option>
                      {field.options?.map((opt, oi) => (
                        <option key={oi} value={opt}>{opt}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={field.type}
                      id={field.name}
                      name={field.name}
                      required={field.required}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:outline-none"
                      style={{
                        borderColor: `${recipe.colors.textMuted}40`,
                        borderRadius: recipe.borders.inputRadius,
                      }}
                    />
                  )}
                </div>
              )) || (
                // Default form fields
                <>
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium mb-1" style={{ color: recipe.colors.text }}>
                      Name<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2 border rounded-lg"
                      style={{ borderColor: `${recipe.colors.textMuted}40`, borderRadius: recipe.borders.inputRadius }}
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-1" style={{ color: recipe.colors.text }}>
                      Email<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2 border rounded-lg"
                      style={{ borderColor: `${recipe.colors.textMuted}40`, borderRadius: recipe.borders.inputRadius }}
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium mb-1" style={{ color: recipe.colors.text }}>
                      Phone
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      className="w-full px-4 py-2 border rounded-lg"
                      style={{ borderColor: `${recipe.colors.textMuted}40`, borderRadius: recipe.borders.inputRadius }}
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium mb-1" style={{ color: recipe.colors.text }}>
                      Message<span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={4}
                      className="w-full px-4 py-2 border rounded-lg"
                      style={{ borderColor: `${recipe.colors.textMuted}40`, borderRadius: recipe.borders.inputRadius }}
                    />
                  </div>
                </>
              )}
              <button
                type="submit"
                className="w-full py-3 font-semibold rounded-lg text-white transition-transform hover:scale-[1.02]"
                style={{
                  backgroundColor: recipe.colors.primary,
                  borderRadius: recipe.borders.buttonRadius,
                }}
              >
                Send Message
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// MINIMAL VARIANT
// =============================================================================

function ContactMinimal({ data, ctx, elements }: ContactProps) {
  const { recipe } = ctx;

  const headlineEl = findElement(elements, 'headline');

  return (
    <section
      id="contact"
      className="py-20 lg:py-28"
      style={{
        backgroundColor: recipe.colors.background,
        fontFamily: `${recipe.typography.bodyFont}, sans-serif`,
      }}
    >
      <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {headlineEl ? (
          <EditableText
            element={headlineEl}
            as="h2"
            className="text-2xl md:text-3xl font-bold mb-8"
            style={{
              color: recipe.colors.text,
              fontFamily: `${recipe.typography.headingFont}, sans-serif`,
            }}
          />
        ) : data.headline ? (
          <h2
            className="text-2xl md:text-3xl font-bold mb-8"
            style={{
              color: recipe.colors.text,
              fontFamily: `${recipe.typography.headingFont}, sans-serif`,
            }}
          >
            {data.headline}
          </h2>
        ) : null}

        <form action={data.formEndpoint} method="POST" className="space-y-4">
          <input
            type="text"
            name="name"
            placeholder="Your name"
            required
            className="w-full px-4 py-3 border rounded-lg"
            style={{ borderColor: `${recipe.colors.textMuted}40`, borderRadius: recipe.borders.inputRadius }}
          />
          <input
            type="email"
            name="email"
            placeholder="Your email"
            required
            className="w-full px-4 py-3 border rounded-lg"
            style={{ borderColor: `${recipe.colors.textMuted}40`, borderRadius: recipe.borders.inputRadius }}
          />
          <textarea
            name="message"
            placeholder="Your message"
            required
            rows={4}
            className="w-full px-4 py-3 border rounded-lg"
            style={{ borderColor: `${recipe.colors.textMuted}40`, borderRadius: recipe.borders.inputRadius }}
          />
          <button
            type="submit"
            className="w-full py-3 font-semibold rounded-lg text-white"
            style={{
              backgroundColor: recipe.colors.primary,
              borderRadius: recipe.borders.buttonRadius,
            }}
          >
            Send Message
          </button>
        </form>
      </div>
    </section>
  );
}

// =============================================================================
// SPLIT VARIANT
// =============================================================================

function ContactSplit({ data, ctx, elements }: ContactProps) {
  const { recipe } = ctx;

  const headlineEl = findElement(elements, 'headline');
  const subheadlineEl = findElement(elements, 'subheadline');
  const splitImageEl = findElement(elements, 'splitImage');

  return (
    <section
      id="contact"
      className="py-0 lg:py-0"
      style={{ fontFamily: `${recipe.typography.bodyFont}, sans-serif` }}
    >
      <div className="grid lg:grid-cols-2">
        {/* Form Side */}
        <div
          className="px-8 lg:px-16 py-16 lg:py-24"
          style={{ backgroundColor: recipe.colors.surface }}
        >
          <div className="max-w-lg">
            {headlineEl ? (
              <EditableText
                element={headlineEl}
                as="h2"
                className="text-3xl md:text-4xl font-bold mb-4"
                style={{
                  color: recipe.colors.text,
                  fontFamily: `${recipe.typography.headingFont}, sans-serif`,
                }}
              />
            ) : data.headline ? (
              <h2
                className="text-3xl md:text-4xl font-bold mb-4"
                style={{
                  color: recipe.colors.text,
                  fontFamily: `${recipe.typography.headingFont}, sans-serif`,
                }}
              >
                {data.headline}
              </h2>
            ) : null}

            {subheadlineEl ? (
              <EditableText
                element={subheadlineEl}
                as="p"
                className="text-lg mb-8"
                style={{ color: recipe.colors.textMuted }}
              />
            ) : data.subheadline ? (
              <p className="text-lg mb-8" style={{ color: recipe.colors.textMuted }}>
                {data.subheadline}
              </p>
            ) : null}

            <form action={data.formEndpoint} method="POST" className="space-y-4">
              <input
                type="text"
                name="name"
                placeholder="Your name"
                required
                className="w-full px-4 py-3 border rounded-lg"
                style={{
                  borderColor: `${recipe.colors.textMuted}40`,
                  borderRadius: recipe.borders.inputRadius,
                  backgroundColor: recipe.colors.background,
                }}
              />
              <input
                type="email"
                name="email"
                placeholder="Your email"
                required
                className="w-full px-4 py-3 border rounded-lg"
                style={{
                  borderColor: `${recipe.colors.textMuted}40`,
                  borderRadius: recipe.borders.inputRadius,
                  backgroundColor: recipe.colors.background,
                }}
              />
              <textarea
                name="message"
                placeholder="Your message"
                required
                rows={4}
                className="w-full px-4 py-3 border rounded-lg"
                style={{
                  borderColor: `${recipe.colors.textMuted}40`,
                  borderRadius: recipe.borders.inputRadius,
                  backgroundColor: recipe.colors.background,
                }}
              />
              <button
                type="submit"
                className="w-full py-3 font-semibold rounded-lg text-white"
                style={{
                  backgroundColor: recipe.colors.primary,
                  borderRadius: recipe.borders.buttonRadius,
                }}
              >
                Send Message
              </button>
            </form>
          </div>
        </div>

        {/* Image Side */}
        <div className="relative min-h-[400px] lg:min-h-full">
          {splitImageEl ? (
            <EditableImage
              element={splitImageEl}
              alt={data.headline || 'Contact'}
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : data.splitImage ? (
            <img
              src={data.splitImage}
              alt={data.headline || 'Contact'}
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : data.mapEmbedUrl ? (
            <iframe
              src={data.mapEmbedUrl}
              width="100%"
              height="100%"
              style={{ border: 0, position: 'absolute', inset: 0 }}
              allowFullScreen
              loading="lazy"
              title="Location Map"
            />
          ) : (
            <div
              className="absolute inset-0"
              style={{ backgroundColor: recipe.colors.primary }}
            />
          )}
        </div>
      </div>
    </section>
  );
}

export default Contact;
